// src/components/admin/text-input.tsx
import type { InputProps } from "ra-core";
import { useInput, useResourceContext, FieldTitle } from "ra-core";
import {
  FormError,
  FormField,
} from "@/components/admin/form";
import { Textarea } from "@/components/ui/textarea";
import { InputHelperText } from "@/components/admin/input-helper-text";
import { cn } from "@/lib/utils";

export type TextInputProps = InputProps & {
  multiline?: boolean;
  inputClassName?: string;
} & React.ComponentProps<"textarea"> &
  React.ComponentProps<"input">;

export const TextInput = (props: TextInputProps) => {
  const resource = useResourceContext(props);
  const {
    label,
    source,
    multiline,
    className,
    inputClassName,
    validate: _validateProp,
    format: _formatProp,
    ...rest
  } = props;
  const { id, field, isRequired } = useInput(props);

  const hasLabel = label !== false;

  return (
    <FormField id={id} className={className} name={field.name}>
      <div className="relative w-full">
        {/* The Fieldset creates the border */}
        <fieldset
          className={cn(
            "group rounded-md border border-input px-3 pb-2 transition-colors focus-within:border-primary focus-within:ring-1 focus-within:ring-primary",
            props.disabled && "opacity-50 cursor-not-allowed",
            multiline ? "min-h-[100px]" : "h-12"
          )}
        >
          {/* The Legend breaks the border */}
          {hasLabel && (
            <legend className="px-1 text-xs text-muted-foreground">
              <FieldTitle
                label={label}
                source={source}
                resource={resource}
                isRequired={isRequired}
              />
            </legend>
          )}

          {/* The actual input (borderless) */}
          {multiline ? (
            <Textarea
              {...rest}
              {...field}
              className={cn(
                "w-full border-0 bg-transparent p-0 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 resize-none",
                inputClassName
              )}
            />
          ) : (
            <input
              {...rest}
              {...field}
              className={cn(
                "w-full border-0 bg-transparent p-0 text-sm shadow-none outline-none focus:ring-0",
                inputClassName
              )}
            />
          )}
        </fieldset>
      </div>

      <InputHelperText helperText={props.helperText} />
      <FormError />
    </FormField>
  );
};